/**
 * TASKS-V2 E1 — Hero "Foco de Hoje".
 *
 * Card grande full-width logo após o título da seção, antes dos
 * Stats menores. Mostra 1 task escolhida via pickFocusTask + 3 CTAs
 * (começar agora, marcar concluída, pular).
 *
 * Estado vazio (sem tasks elegíveis): CTA pra abrir chat focado em
 * tasks (próxima sessão pode amplificar).
 *
 * Telemetria via executionTrace.js (não recebe trace por prop pra
 * facilitar uso em qualquer container).
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Play, Check, SkipForward, Flag, MessageSquare, Focus } from "lucide-react";
import { pickFocusTask } from "@/utils/taskGrouping";
import { formatCountdown, URGENCY_COLOR, formatAbsoluteTooltip } from "@/utils/taskCountdown";
import trace from "@/services/executionTrace";

const PRIORITY_BADGE = {
  high: { label: "Alta", cls: "bg-red-500/10 text-red-400 border-red-500/20" },
  medium: { label: "Média", cls: "bg-amber-500/10 text-amber-400 border-amber-500/20" },
  low: { label: "Baixa", cls: "bg-slate-500/10 text-slate-400 border-slate-500/20" },
};

export default function HeroFocusCard({
  tasks,
  onStart,
  onComplete,
  onAskNexo,
  // PRODUTO-V1 — Modo Foco. Quando ausente, botão fica oculto (compat
  // TASKS-V2).
  onEnterFocus,
}) {
  const [skipIds, setSkipIds] = useState([]);

  const focus = useMemo(
    () => pickFocusTask(tasks, new Date(), skipIds),
    [tasks, skipIds],
  );

  const countdown = useMemo(
    () => (focus ? formatCountdown(focus.datetime_due || focus.due_date) : null),
    [focus],
  );

  // Trace de "shown" 1× por focus task (não em toda re-render).
  useEffect(() => {
    if (!focus) return;
    try {
      trace.record("tasks.hero.shown", {
        focus_task_priority: focus.priority || "medium",
        focus_task_urgency: countdown?.urgency || "none",
      });
    } catch {/* */}
  }, [focus?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStart = useCallback(() => {
    if (!focus) return;
    try { trace.record("tasks.hero.start", { task_id_hash: String(focus.id).slice(0, 8) }); } catch {/* */}
    onStart?.(focus);
  }, [focus, onStart]);

  const handleComplete = useCallback(() => {
    if (!focus) return;
    try { trace.record("tasks.hero.complete", { task_id_hash: String(focus.id).slice(0, 8) }); } catch {/* */}
    onComplete?.(focus);
  }, [focus, onComplete]);

  const handleSkip = useCallback(() => {
    if (!focus) return;
    try { trace.record("tasks.hero.skip", { skipped_count: skipIds.length + 1 }); } catch {/* */}
    setSkipIds((prev) => [...prev, focus.id]);
  }, [focus, skipIds.length]);

  const handleEmptyCta = useCallback(() => {
    try { trace.record("tasks.hero.empty_cta", {}); } catch {/* */}
    onAskNexo?.();
  }, [onAskNexo]);

  const handleEnterFocus = useCallback(() => {
    if (!focus) return;
    try {
      trace.record("tasks.hero.enter_focus", {
        focus_task_priority: focus.priority || "medium",
      });
    } catch {/* */}
    onEnterFocus?.(focus);
  }, [focus, onEnterFocus]);

  if (!focus) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card rounded-2xl p-6 mb-4 border border-cyan-500/10"
        data-testid="hero-focus-empty"
      >
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-wider text-cyan-400 mb-1">
              Foco de hoje
            </p>
            <p className="text-base text-slate-300">
              Nada urgente agora. Adicione uma tarefa ou pergunte ao Nexo o que priorizar.
            </p>
          </div>
          <button
            type="button"
            onClick={handleEmptyCta}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 hover:bg-cyan-500/15 transition-all text-sm font-medium"
            data-testid="hero-focus-ask-nexo"
          >
            <MessageSquare className="w-4 h-4" />
            Falar com Nexo
          </button>
        </div>
      </motion.div>
    );
  }

  const prio = PRIORITY_BADGE[focus.priority || "medium"] || PRIORITY_BADGE.medium;
  const urgencyCls = countdown ? URGENCY_COLOR[countdown.urgency] : "text-slate-500";

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-card rounded-2xl p-6 mb-4 border border-cyan-500/20 shadow-[0_0_30px_-15px_rgba(6,182,212,0.4)]"
      data-testid="hero-focus-card"
    >
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-xs uppercase tracking-wider text-cyan-400 mb-2">
            Foco de hoje
          </p>
          <h2
            className="text-xl md:text-2xl font-bold text-white mb-2 truncate"
            data-testid="hero-focus-title"
          >
            {focus.title}
          </h2>
          {countdown && (
            <p
              className={`text-base ${urgencyCls} mb-3`}
              title={formatAbsoluteTooltip(focus.datetime_due || focus.due_date)}
              data-testid="hero-focus-countdown"
            >
              {countdown.urgency === "overdue" ? "● " : ""}{countdown.text}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-2">
            {focus.priority && focus.priority !== "medium" && (
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium border ${prio.cls}`}
              >
                <Flag className="w-3 h-3" />
                {prio.label}
              </span>
            )}
            {focus.project_id && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-slate-500/10 text-slate-400 border border-slate-500/20">
                Projeto
              </span>
            )}
            {focus.estimated_minutes && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-slate-500/10 text-slate-400 border border-slate-500/20">
                ~{focus.estimated_minutes}min
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 shrink-0">
          {focus.status !== "doing" && (
            <motion.button
              type="button"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleStart}
              className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/20 transition-all text-sm font-medium shadow-[0_0_20px_-10px_rgba(6,182,212,0.6)]"
              data-testid="hero-focus-start"
            >
              <Play className="w-4 h-4" />
              Começar agora
            </motion.button>
          )}
          {onEnterFocus && (
            <motion.button
              type="button"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleEnterFocus}
              className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-violet-300 bg-violet-500/10 border border-violet-500/20 hover:bg-violet-500/15 transition-colors"
              data-testid="hero-focus-enter-focus"
            >
              <Focus className="w-4 h-4" />
              Modo Foco
            </motion.button>
          )}
          <button
            type="button"
            onClick={handleComplete}
            className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-slate-400 hover:text-emerald-400 transition-colors"
            data-testid="hero-focus-complete"
          >
            <Check className="w-4 h-4" />
            Marcar concluída
          </button>
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-white/[0.04]">
        <button
          type="button"
          onClick={handleSkip}
          className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-300 transition-colors"
          data-testid="hero-focus-skip"
        >
          <SkipForward className="w-3 h-3" />
          Pular: próxima recomendação →
        </button>
      </div>
    </motion.div>
  );
}
